// 📐 #6431 받기 엔진 «가운데 줄 쏠림» 피하기 — 가운데(오른쪽) 정렬 글 노드가 3줄 이상이면 그 글을 어절마다 따로 글 노드로 그린다.
//
// ★왜★ 받기 엔진(@remotion/web-renderer 4.0.397 `drawing/text/find-line-breaks.text.js`)은 글 노드 하나를 줄로 나눌 때
//   줄의 가로 위치를 «그 줄까지 쌓은 글 전체 상자의 왼쪽 끝»으로 잡는다. 첫 줄·끝 줄은 맞지만, 가운데 줄은 앞 줄보다 짧으면
//   앞 줄 왼쪽 끝에 붙어 그려진다(10-10 실측: 강의 브리핑 단계 칸 «눈으로»·«라바콘과» · 큰 제목 3줄 둘째 줄 132px).
//   미리보기(브라우저)는 맞다 — 받은 영상만 틀린다.
// ★어떻게★ 글 노드가 한 줄이면 엔진은 그 노드 자기 상자 왼쪽에 그린다. 어절마다 따로 노드면 어느 어절도 두 줄에 걸치지 않으므로
//   줄 위치가 브라우저와 같다. 노드를 나눠도 브라우저 배치는 한 글자도 안 바뀐다(같은 인라인 흐름) → 다시 재도 같은 결과(출렁임 없음).
// ★바이트★ 3줄 미만·왼쪽 정렬 글은 손대지 않는다(이미 맞는 장면은 종전과 같은 노드 = 같은 그림). 재지 못하는 곳(jsdom·서버 정적 HTML)도 그대로.
// ★표식 찾기★ 장표 층에 `display:none` 표식 하나를 두고 그 부모 아래 글 노드를 잰다 — 엔진은 display:none 을 건너뛴다(그림 0).
import { createContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** 어절마다 따로 노드로 그릴 글(원문 문자열). `GlueText` 가 읽는다. 없으면(null) 종전. */
export const EngineWordNodesContext = createContext<ReadonlySet<string> | null>(null);

/** 엔진이 쌓은 상자 왼쪽 끝으로 그리는 정렬 — 왼쪽(start·left·justify 의 앞줄)은 모든 줄 왼쪽이 같아 틀리지 않는다. */
const SHIFTING_ALIGN = /^(center|right|end|-webkit-center|-webkit-right)$/;
/** 엔진이 가운데 줄을 틀리게 놓기 시작하는 줄 수. */
export const ENGINE_SHIFT_MIN_LINES = 3;

/** 글 노드 줄 상자(브라우저 배치) → 서로 다른 줄 수. 폭 0 상자(접힌 띄어쓰기)는 세지 않는다. */
export function countLines(rects: ReadonlyArray<{ top: number; height: number; width: number }>): number {
  const mids: number[] = [];
  for (const r of rects) {
    if (!(r.width > 0)) continue;
    const mid = r.top + r.height / 2;
    if (!mids.some((m) => Math.abs(m - mid) < Math.max(1, r.height * 0.5))) mids.push(mid);
  }
  return mids.length;
}

/** `root` 아래에서 엔진이 가운데 줄을 틀리게 놓을 글 노드의 글(원문). */
export function findShiftingTexts(root: Element): string[] {
  const doc = root.ownerDocument;
  if (!doc || typeof doc.createRange !== 'function') return [];
  const out: string[] = [];
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const rg = doc.createRange();
  if (typeof rg.getClientRects !== 'function') return [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const text = n.nodeValue ?? '';
    const parent = n.parentElement;
    if (!text.trim() || !parent) continue;
    if (!SHIFTING_ALIGN.test(getComputedStyle(parent).textAlign)) continue;
    rg.selectNodeContents(n);
    if (countLines(Array.from(rg.getClientRects())) >= ENGINE_SHIFT_MIN_LINES) out.push(text);
  }
  return out;
}

/** 배치가 없는 곳(서버 렌더 · jsdom 시험)은 재지 않는다(`Overlay169SlideLayer` 관용구). */
const useMeasureEffect = typeof window === 'undefined' || /jsdom/i.test(typeof navigator === 'undefined' ? '' : navigator.userAgent) ? useEffect : useLayoutEffect;

/**
 * 장표 층 훅 — `[표식 ref, 어절 노드로 그릴 글 집합]`. 표식(`display:none` span)의 부모 아래를 매 렌더 재서, 새로 걸린 글만 더한다.
 *   한 번 더한 글은 빼지 않는다(어절 노드로 그리면 노드가 한 줄씩이라 다시 재면 안 걸린다 · 어절 노드는 어느 배치에서도 맞다).
 */
export function useEngineWordNodes<E extends HTMLElement = HTMLSpanElement>() {
  const ref = useRef<E>(null);
  const [set, setSet] = useState<ReadonlySet<string> | null>(null);
  useMeasureEffect(() => {
    const root = ref.current?.parentElement;
    if (!root) return;
    const add = findShiftingTexts(root).filter((t) => !set?.has(t));
    if (add.length) setSet((prev) => new Set([...(prev ?? []), ...add]));
  });
  // ref 타입은 useRef 가 돌려준 그대로 — @types/react 18(RefObject<E>)·19(RefObject<E | null>) 둘 다 `ref={…}` 에 맞는다(title-balance 와 같은 꼴).
  const result: [typeof ref, ReadonlySet<string> | null] = [ref, set];
  return result;
}
